import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const faqs = [
  {
    q: 'What NEET score do I need for MBBS admission in a UP private college?',
    a: 'UP private colleges admit students across all NEET score brackets through Management and NRI quota. Even a score of 180–250 can secure a seat. Government college cut-offs are higher — General category typically needs 450+ for UP state quota. We assess your eligibility precisely based on your score, category, and budget.',
  },
  {
    q: 'What is the total fee for MBBS in a private college in UP?',
    a: 'First-year fees at UP private medical colleges typically range from ₹18–24 lakh. Total course package (4.5 years + internship) ranges from ₹75–95 lakh depending on the college. Colleges like Rohilkhand Medical, Rajshree, and Era fall in the ₹80–90 lakh range. We maintain a current fee structure for every college we work with.',
  },
  {
    q: 'Can I get BAMS admission with a low NEET score?',
    a: 'Yes. BAMS private colleges in UP and Uttarakhand have lower cut-offs than MBBS. Seat intake ranges from 60–100 per college. Management quota seats are available even for students with NEET scores below 200. Uttarakhand follows an open-state policy, which means students from any state can apply.',
  },
  {
    q: 'What about MBBS in Nepal — is it NMC-recognised?',
    a: 'Yes. All colleges we recommend in Nepal are affiliated with Kathmandu University and recognised by the National Medical Commission (NMC), India. This means graduates can sit FMGE (Foreign Medical Graduate Examination) and practice in India. Total fees are often lower than comparable Indian private college packages.',
  },
  {
    q: 'Do you charge any advance fees before admission?',
    a: 'No advance payment is required. We provide complete guidance — eligibility assessment, college shortlist, document preparation, and counseling support — before you commit to any payment. Fees are collected only after your seat is confirmed in writing by the college.',
  },
  {
    q: 'How long does the full admission process take?',
    a: 'Once we have your NEET scorecard and documents, most admissions are completed within 2–4 weeks. During the main NEET counseling season (August–October), we run parallel tracks across all counseling rounds to maximise your chances without losing time.',
  },
];

const Item = ({ faq, isOpen, onToggle }) => (
  <div className="border-b border-slate-200 last:border-0">
    <button
      onClick={onToggle}
      className="w-full flex items-start justify-between gap-4 py-5 text-left group"
      aria-expanded={isOpen}
    >
      <span className="text-sm font-semibold text-gray-900 group-hover:text-primary-800 transition-colors duration-150 leading-snug">
        {faq.q}
      </span>
      <span
        className={`shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center transition-transform duration-200 ${
          isOpen ? 'bg-primary-800 text-white rotate-45' : 'bg-slate-100 text-gray-500'
        }`}
        aria-hidden="true"
      >
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
      </span>
    </button>

    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p className="pb-5 text-sm text-gray-600 leading-relaxed pr-8">
            {faq.a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQ = () => {
  const [open, setOpen] = useState(null);

  return (
    <section className="py-16 md:py-24 bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">

          {/* Left — heading + contact nudge */}
          <div className="lg:sticky lg:top-24">
            <h2 className="text-section font-bold text-gray-900" style={{ textWrap: 'balance' }}>
              Common Questions
            </h2>
            <p className="mt-4 text-gray-600 text-base leading-relaxed">
              Everything families ask before starting the admission process. Don't see yours here?
            </p>
            <a
              href="#contact"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-900 transition-colors duration-150"
            >
              Ask us directly
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </a>

            <div className="mt-8 p-5 bg-white rounded-xl border border-slate-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Quick facts</p>
              <ul className="space-y-2">
                {[
                  '70+ medical colleges in UP',
                  '8,485 MBBS seats (UP, 2024)',
                  '4 counseling rounds per cycle',
                  'NMC-recognised Nepal colleges',
                  'No advance payment, ever',
                ].map((fact, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary-600 shrink-0" />
                    {fact}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right — accordion */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 px-7 py-2">
            {faqs.map((faq, i) => (
              <Item
                key={i}
                faq={faq}
                isOpen={open === i}
                onToggle={() => setOpen(open === i ? null : i)}
              />
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

export default FAQ;
